import React, { useEffect, useState } from "react";
import { motion } from "motion/react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import adminUserRequests from "../../api/AdminUserRequests";
import Loader from "../../components/utils/Loader";
import Navbar from "../../components/dashboard/Navbar";
import AdminStats from "../../components/admins/AdminStats";
import AdminCard from "../../components/admins/AdminCard";
import AdminTable from "../../components/admins/AdminTable";
import AdminSearch from "../../components/admins/AdminSearch";
import DeleteAdminModal from "../../components/admins/DeleteAdminModal";
import AdminPermissionsModal from "../../components/admins/AdminPermissionsModal";
import StatusChangeModal from "../../components/admins/StatusChangeModal";
import { Users, Plus, AlertCircle, LayoutGrid, Table2 } from "lucide-react";

const Admins = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [admins, setAdmins] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [adminToDelete, setAdminToDelete] = useState(null);
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [viewMode, setViewMode] = useState("table"); // "table" | "card"
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [adminToChangeStatus, setAdminToChangeStatus] = useState(null);
  const [pendingNewStatus, setPendingNewStatus] = useState(null);
  const [statusChangeLoading, setStatusChangeLoading] = useState(false);

  // Fetch admins (silent = true skips full-page loading for background refresh)
  const fetchAdmins = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const response = await adminUserRequests.getAdminUser();
      const data = response.data.data;

      if (data.status_code === "000") {
        const adminsData = Array.isArray(data.data) ? data.data : [];
        setAdmins(adminsData);
      } else {
        if (!silent) toast.error(response.data.reason || "Failed to fetch administrators");
        setAdmins([]);
      }
    } catch (error) {
      console.error("Error fetching admins:", error);
      if (!silent) toast.error("Failed to fetch administrators");
      setAdmins([]);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    fetchAdmins();
  }, []);

  const handleEditAdmin = (admin) => {
    navigate(`/admins/edit/${admin.admin_slug}`);
  };

  // View permissions handler
  const handleViewPermissions = (admin) => {
    setSelectedAdmin(admin);
    setShowPermissionsModal(true);
  };

  // Open status change confirmation modal
  const handleToggleStatus = (admin) => {
    const currentStatus =
      admin.status === true ||
      admin.status === 1 ||
      admin.status === "1" ||
      admin.status === "active";
    setAdminToChangeStatus(admin);
    setPendingNewStatus(!currentStatus);
    setShowStatusModal(true);
  };

  const closeStatusModal = () => {
    setShowStatusModal(false);
    setAdminToChangeStatus(null);
    setPendingNewStatus(null);
  };

  // Confirm status change (from modal) — then refresh list
  const handleConfirmStatusChange = async (admin, newStatus) => {
    if (!admin) return;
    setStatusChangeLoading(true);
    try {
      const payload = { status: newStatus, admin_slug: admin.admin_slug };
      const response = await adminUserRequests.updateAccountStatus(payload);

      if (response.success) {
        toast.success(
          `Administrator ${newStatus ? "approved" : "disapproved"} successfully!`
        );
        closeStatusModal();
        await fetchAdmins(true); // background refresh so list updates without full reload
      } else {
        toast.error(response.message || "Failed to update administrator status");
      }
    } catch (error) {
      console.error("Error updating admin status:", error);
      toast.error("Failed to update administrator status");
    } finally {
      setStatusChangeLoading(false);
    }
  };

  // Close permissions modal
  const closePermissionsModal = () => {
    setShowPermissionsModal(false);
    setSelectedAdmin(null);
  };

  // const handleViewAdmin = (admin) => {
  //   navigate(`/admins/view/${admin.id}`);
  // };

  const openDeleteModal = (admin) => {
    setAdminToDelete(admin);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setAdminToDelete(null);
  };

  const handleDeleteAdmin = async () => {
    if (!adminToDelete) return;

    setDeleteLoading(true);
    try {
      const payload = {
        admin_slug: adminToDelete.admin_slug,
      };
      const response = await adminUserRequests.deleteTeamMember(payload);

      if (response.success) {
        toast.success("Administrator deleted successfully!");
        closeDeleteModal();
        fetchAdmins();
      } else {
        toast.error(response.message || "Failed to delete administrator");
      }
    } catch (error) {
      console.error("Error deleting admin:", error);
      toast.error("Failed to delete administrator");
    } finally {
      setDeleteLoading(false);
    }
  };

  const adminsArray = Array.isArray(admins) ? admins : [];
  let filteredAdmins = [];
  try {
    filteredAdmins = adminsArray.filter(
      (admin) =>
        admin?.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        admin?.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        admin?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        admin?.role?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  } catch (error) {
    console.error("Error filtering admins:", error);
    filteredAdmins = [];
  }

  const tabVariants = {
    hidden: {
      opacity: 0,
      x: 20,
      scale: 0.95,
    },
    visible: {
      opacity: 1,
      x: 0,
      scale: 1,
      transition: {
        duration: 0.3,
        ease: "easeOut",
      },
    },
    exit: {
      opacity: 0,
      x: -20,
      scale: 0.95,
      transition: {
        duration: 0.2,
        ease: "easeIn",
      },
    },
  };

  if (loading && admins.length === 0) {
    return (
      <>
        <Navbar />
        <div className="flex justify-center items-center min-h-screen bg-gradient-to-br via-blue-50 to-indigo-50 from-slate-50">
          <Loader />
        </div>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gradient-to-br via-blue-50 to-indigo-50 from-slate-50">
        <div className="px-4 py-6 mx-auto max-w-8xl sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">
                  System Administrators
                </h1>
                <p className="mt-2 text-gray-600">
                  Manage administrators and their access permissions
                </p>
              </div>
              <div className="flex gap-3">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => navigate("/admins/add")}
                  className="flex gap-2 items-center px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg shadow-md hover:from-blue-700 hover:to-indigo-700"
                >
                  <Plus className="w-4 h-4" />
                  Add Admin
                </motion.button>
              </div>
            </div>
          </div>

          {/* Stats Cards — use same isActive logic as AdminCard/AdminTable */}
          <AdminStats
            totalAdmins={admins.length}
            activeAdmins={
              admins.filter(
                (admin) =>
                  admin.status === true ||
                  admin.status === 1 ||
                  admin.status === "1" ||
                  admin.status === "active"
              ).length
            }
            inactiveAdmins={
              admins.length -
              admins.filter(
                (admin) =>
                  admin.status === true ||
                  admin.status === 1 ||
                  admin.status === "1" ||
                  admin.status === "active"
              ).length
            }
          />

          {/* Admins Section */}
          <motion.div
            variants={tabVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="mx-auto w-full max-w-full md:max-w-8xl"
          >
            <div className="overflow-hidden rounded-2xl border border-gray-200/80 bg-white shadow-lg ring-1 ring-gray-100 sm:rounded-2xl">
              {/* Toolbar: header row + search row */}
              <div className="border-b border-gray-100 bg-gradient-to-r from-gray-50/80 to-white">
                <div className="p-4 sm:p-6">
                  {/* Row 1: Title + View toggle */}
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-blue-100">
                        <Users className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <h2 className="text-lg font-semibold text-gray-900">
                          Administrators list
                        </h2>
                        <p className="text-sm text-gray-500">
                          Search and manage accounts
                        </p>
                      </div>
                    </div>
                    <div className="inline-flex rounded-xl border border-gray-200 bg-gray-50/60 p-1">
                      <motion.button
                        type="button"
                        onClick={() => setViewMode("table")}
                        className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-all ${
                          viewMode === "table"
                            ? "bg-white text-blue-600 shadow-sm ring-1 ring-gray-200"
                            : "text-gray-600 hover:text-gray-900"
                        }`}
                      >
                        <Table2 className="h-4 w-4" />
                        Table
                      </motion.button>
                      <motion.button
                        type="button"
                        onClick={() => setViewMode("card")}
                        className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-all ${
                          viewMode === "card"
                            ? "bg-white text-blue-600 shadow-sm ring-1 ring-gray-200"
                            : "text-gray-600 hover:text-gray-900"
                        }`}
                      >
                        <LayoutGrid className="h-4 w-4" />
                        Cards
                      </motion.button>
                    </div>
                  </div>
                  {/* Row 2: Search + count */}
                  <div className="mt-4">
                    <AdminSearch
                      searchTerm={searchTerm}
                      onSearchChange={setSearchTerm}
                      totalCount={(admins || []).length}
                      filteredCount={filteredAdmins.length}
                    />
                  </div>
                </div>
              </div>

              {/* Admins List — Table or Cards */}
              <div className="p-4 sm:p-8">
                {filteredAdmins.length > 0 ? (
                  viewMode === "table" ? (
                    <AdminTable
                      admins={filteredAdmins}
                      onEdit={handleEditAdmin}
                      onDelete={openDeleteModal}
                      onViewPermissions={handleViewPermissions}
                      onToggleStatus={handleToggleStatus}
                    />
                  ) : (
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                      {filteredAdmins.map((admin, index) => (
                        <AdminCard
                          key={admin.admin_slug || index}
                          admin={admin}
                          onEdit={handleEditAdmin}
                          onDelete={openDeleteModal}
                          onViewPermissions={handleViewPermissions}
                          onToggleStatus={handleToggleStatus}
                        />
                      ))}
                    </div>
                  )
                ) : (
                  <div className="py-12 text-center">
                    <AlertCircle className="mx-auto w-12 h-12 text-gray-400" />
                    <h3 className="mt-4 text-lg font-medium text-gray-900">
                      No admins found
                    </h3>
                    <p className="mt-2 text-gray-500">
                      {searchTerm
                        ? "Try adjusting your search criteria."
                        : "No administrators have been added yet."}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <DeleteAdminModal
        isOpen={showDeleteModal}
        adminToDelete={adminToDelete}
        onClose={closeDeleteModal}
        onConfirm={handleDeleteAdmin}
        loading={deleteLoading}
      />

      {/* Admin Permissions Modal */}
      <AdminPermissionsModal
        isOpen={showPermissionsModal}
        admin={selectedAdmin}
        onClose={closePermissionsModal}
      />

      {/* Status change confirmation (Approve / Disapprove) */}
      <StatusChangeModal
        isOpen={showStatusModal}
        admin={adminToChangeStatus}
        newStatus={pendingNewStatus}
        onClose={closeStatusModal}
        onConfirm={handleConfirmStatusChange}
        loading={statusChangeLoading}
      />
    </>
  );
};

export default Admins;
